import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createSubscriptionQuery } from 'shared/lib/@tanstack-query';
import {
  getFilteredTasks,
  TaskFilterOptions,
} from 'shared/services/backend-api';
import { ID } from 'shared/types/id';
import { chunkArray } from 'shared/utils/chunk-array';

export type { TaskFilterOptions };

export const queryKey = 'filtered-tasks';

const shouldSplitQueries = (filters: TaskFilterOptions): boolean => {
  const { goals, lifeAreas, ids } = filters;

  if (
    [goals?.length, lifeAreas?.length, ids?.length].filter(Boolean).length > 1
  ) {
    return true;
  }

  return (
    (goals?.length ?? 0) > 30 ||
    (lifeAreas?.length ?? 0) > 30 ||
    (ids?.length ?? 0) > 30
  );
};

export const useFilteredTasksQuery = (
  uid: ID | undefined,
  filters: TaskFilterOptions,
  enabled = true,
) => {
  const splicedQueries = shouldSplitQueries(filters);

  const queryResult = useQuery({
    queryKey: [
      queryKey,
      uid ?? '',
      enabled.toString(),
      ...Object.entries(filters)
        .filter(([, value]) => value !== undefined)
        .map(
          ([key, value]) =>
            `${key}: ${
              value instanceof Date
                ? value.getTime().toString()
                : Array.isArray(value)
                  ? value.toSorted().join(',')
                  : value // value?.toString()
            }`,
        ),
    ],
    queryFn: (context) => {
      if (!enabled || !uid) {
        return [];
      }

      const { goals, lifeAreas, ids, ...rest } = filters;
      const splitQueries = shouldSplitQueries(filters);
      const queries = splitQueries
        ? []
        : [() => getFilteredTasks(uid, filters)];

      if (splitQueries && lifeAreas?.length) {
        const chunkedLifeAreas = chunkArray(lifeAreas.toSorted(), 30);
        chunkedLifeAreas.forEach((chunked) =>
          queries.push(() =>
            // explicitly no goals, as we want specific lifeArea connected tasks
            getFilteredTasks(uid, { ...rest, ids, lifeAreas: chunked }),
          ),
        );
      }

      if (splitQueries && goals?.length) {
        const chunkedGoals = chunkArray(goals.toSorted(), 30);
        chunkedGoals.forEach((chunked) =>
          queries.push(() =>
            // explicitly no lifeAreas, as we want specific goal connected tasks
            getFilteredTasks(uid, { ...rest, ids, goals: chunked }),
          ),
        );
      }

      if (splitQueries && ids?.length) {
        const chunkedIds = chunkArray(ids.toSorted(), 30);
        chunkedIds.forEach((chunked) =>
          queries.push(() =>
            // can't add goals or lifeAreas, as it will cause problems with firestore
            getFilteredTasks(uid, { ...rest, ids: chunked }),
          ),
        );
      }

      return createSubscriptionQuery(queries)(context);
    },
    placeholderData: (previous) => previous ?? [],
    enabled: !!uid && enabled,
  });

  const processedData = useMemo(() => {
    const data = queryResult.data ?? [];

    if (!splicedQueries) {
      return data;
    }

    if (
      filters.ids?.length &&
      filters.lifeAreas?.length &&
      filters.goals?.length
    ) {
      return data.filter(
        (task) =>
          (filters.ids?.includes(task.id) &&
            task.lifeAreaId !== null &&
            filters.lifeAreas?.includes(task.lifeAreaId!)) ||
          (filters.ids?.includes(task.id) &&
            task.goalId !== null &&
            filters.goals?.includes(task.goalId!)),
      );
    }

    if (filters.ids?.length && filters.lifeAreas?.length) {
      return data.filter(
        (task) =>
          filters.ids?.includes(task.id) &&
          task.lifeAreaId !== null &&
          filters.lifeAreas?.includes(task.lifeAreaId!),
      );
    }

    if (filters.ids?.length && filters.goals?.length) {
      return data.filter(
        (task) =>
          filters.ids?.includes(task.id) &&
          task.goalId !== null &&
          filters.goals?.includes(task.goalId!),
      );
    }

    return data;
  }, [
    filters.goals,
    filters.ids,
    filters.lifeAreas,
    queryResult.data,
    splicedQueries,
  ]);

  return {
    ...queryResult,
    data: processedData,
  };
};
